import { useMemo } from 'react';
import { usePostHog } from 'posthog-js/react';
import { PostHogInstance } from 'shared/posthogCapture/types';
import type { NotificationChannel } from 'shared/hooks/api/notificationSettings';

interface LanguageEditedProperties {
  language_selected: 'en' | 'fr';
}

interface NotificationProperties {
  type: 'new events' | 'shift reports' | 'shift reminders';
}

interface NotificationChannelProperties extends NotificationProperties {
  channel: NotificationChannel;
}

export const createSettingsCaptureEvents = (posthog: PostHogInstance) => ({
  languageEdited: (properties: LanguageEditedProperties) => {
    posthog?.capture('settings / language / edited', properties);
  },
  loggedOutAllDevices: () => {
    posthog?.capture('settings / logout all devices / clicked');
  },
  notificationsChannelChanged: (properties: NotificationChannelProperties) => {
    posthog?.capture('settings / notifications / channel changed', properties);
  },
  notificationsDisabled: (properties: NotificationProperties) => {
    posthog?.capture('settings / notifications / disabled', properties);
  },
  notificationsEnabled: (properties: NotificationProperties) => {
    posthog?.capture('settings / notifications / enabled', properties);
  },
  shiftScheduleEditedDays: () => {
    posthog?.capture('settings / shift schedule / edited days');
  },
  shiftScheduleEditedTime: () => {
    posthog?.capture('settings / shift schedule / edited time');
  },
  usernameEdited: () => {
    posthog?.capture('settings / username / edited');
  },
});

export type SettingsCaptureEvents = ReturnType<
  typeof createSettingsCaptureEvents
>;

export const usePosthogCapture = () => {
  const posthog = usePostHog();
  return useMemo(() => createSettingsCaptureEvents(posthog), [posthog]);
};
